import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePerformGlobalRefresh } from "../../../../components/header/hooks";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { EditDepartmentLocationConfigMode } from "../../../../utils/models";
import { LocationCellProvider } from "../../v2/Cells/LocationCellContext";
import { EditDepartmentLocationConfig } from "../../v2/EditDepartmentLocationConfig/EditDepartmentLocationConfig";
import type { LocationUpdateInfo } from "../../v2/EditDepartmentLocationConfig/useEditDepartmentLocationConfigState";
import type { CoverageRow } from "../types";
import { resolveLocationForCoverageRow } from "./resolveLocationForCoverageRow";

type Props = {
	row: CoverageRow;
	/** Whether THIS row's editor popover is the open one (single-open managed by the grid). */
	isOpen: boolean;
	/** Open/close request for this row (false to close). */
	onOpenChange: (open: boolean) => void;
	/** Sunday-first week-start day (0-6) for the per-day selectors. */
	weekStartDay: number;
	/** Section names already in use — feeds the section dropdown's create-new list. */
	uniqueSections: string[];
	/** Refetch the coverage grid after a save that changed generated shifts. */
	onCoverageRefresh: () => Promise<unknown>;
	/** The Location cell that anchors the popover (mirrors v2's cell-anchored editor). */
	children: ReactNode;
};

/** Opens the shared v2 `EditDepartmentLocationConfig` as a cell-anchored popover (like v2); refreshes the grid on save. */
export const CoverageLocationEditor = ({ row, isOpen, onOpenChange, weekStartDay, uniqueSections, onCoverageRefresh, children }: Props) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const { showSnackbar } = useSnackbar();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const [displayCloseWarning, setDisplayCloseWarning] = useState(false);
	const [isSomeDropdownOpen, setIsSomeDropdownOpen] = useState(false);
	const didEditAnythingRef = useRef(false);

	const resolvedLocation = useMemo(() => resolveLocationForCoverageRow(row, chosenDepartment.locations), [row, chosenDepartment.locations]);

	// Conservative occupied-days signal: weekday indices (Sunday-first) where any cycle
	// week already has a generated shift, so the editor blocks deleting a staffed day.
	const occupiedDays = useMemo<number[]>(() => {
		const result = new Set<number>();
		row.days.forEach((cell, idx) => {
			if (cell.filled > 0) result.add(idx % 7);
		});
		return Array.from(result);
	}, [row.days]);

	const close = useCallback(() => {
		setDisplayCloseWarning(false);
		didEditAnythingRef.current = false;
		onOpenChange(false);
	}, [onOpenChange]);

	// Warn before dropping unsaved edits (ignore close while a dropdown owns the outside-click).
	const handleRequestClose = useCallback(() => {
		if (isSomeDropdownOpen) return;
		if (didEditAnythingRef.current && !displayCloseWarning) {
			setDisplayCloseWarning(true);
			return;
		}
		close();
	}, [isSomeDropdownOpen, displayCloseWarning, close]);

	const handleSave = useCallback(
		async (updateInfo?: LocationUpdateInfo) => {
			if (updateInfo?.refreshDepartmentData) {
				await performGlobalRefresh({ avoidRefreshFor: { nursesData: true, requestsData: true, notificationsData: true } });
			}
			await onCoverageRefresh();
			showSnackbar(t("schedule.template.v3.editor.saved"), "success");
			close();
		},
		[performGlobalRefresh, onCoverageRefresh, showSnackbar, t, close],
	);

	// Outside-config / unresolvable rows have no backing config — render just the anchor.
	if (!resolvedLocation) return <>{children}</>;

	return (
		<>
			<SystemReactTinyPopover
				isOpen={isOpen}
				onClickOutside={handleRequestClose}
				positions={["right"]}
				disabledChildrenWrapper
				data-testid="ms3-location-editor"
				content={
					<LocationCellProvider weekStartDay={weekStartDay}>
						<EditDepartmentLocationConfig
							mode={EditDepartmentLocationConfigMode.Edit}
							existingConfig={resolvedLocation}
							occupiedDays={occupiedDays}
							uniqueSections={uniqueSections}
							isSynergyMS={isMedicalDepartment}
							onDidChangeNotifier={() => {
								didEditAnythingRef.current = true;
							}}
							onSomeDropdownOpenChange={setIsSomeDropdownOpen}
							onCancel={handleRequestClose}
							onSave={handleSave}
						/>
					</LocationCellProvider>
				}
			>
				{children}
			</SystemReactTinyPopover>
			<SystemDialog
				open={displayCloseWarning}
				onClose={() => setDisplayCloseWarning(false)}
				title={t("schedule.template.v3.editor.discard.title")}
				body={t("schedule.template.v3.editor.discard.body")}
				onConfirm={close}
				cancel={t("schedule.template.v3.editor.discard.cancel")}
				confirm={t("schedule.template.v3.editor.discard.confirm")}
				dataTestId="ms3-editor-discard"
			/>
		</>
	);
};
